import {
  type CourseDetailResponse,
  type CourseLocaleData,
  getSession,
  orUndefined,
  type SurveyResponse,
} from '@lms/api-client/backend';
import { LoaderCircleIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import Player from '@/features/scormplayer/components/player';
import { NotFound } from '@/routes/not-found';

export function CoursePlayer() {
  const { sessionId } = useParams<{ sessionId: string }>();

  const { data: session, isLoading } = useSWR(
    sessionId ? ['session', sessionId] : null,
    () => orUndefined(getSession(sessionId ?? '')),
  );

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center bg-white">
        <LoaderCircleIcon className="animate-spin" />
      </div>
    );
  }

  if (session === undefined) {
    return <NotFound />;
  }

  // Course data is already included in the session response (joined at the
  // backend). This avoids a separate API call that would require admin auth.
  const course: CourseDetailResponse = {
    id: session.courseId,
    image: null,
    data: session.courseData as Record<string, CourseLocaleData>,
    locationIds: [session.locationId],
  };

  // Use inlined survey data from the session response.
  let beforeSurvey: SurveyResponse | undefined;
  let afterSurvey: SurveyResponse | undefined;

  if (session.survey) {
    const surveyResponse: SurveyResponse = {
      id: session.survey.id,
      name: session.survey.name,
      createdAt: '',
      questions: session.survey.questions,
    };

    if (!session.survey.beforeCourseAnswered) {
      beforeSurvey = surveyResponse;
    }
    if (!session.survey.afterCourseAnswered) {
      afterSurvey = surveyResponse;
    }
  }

  return (
    <Player
      session={session}
      course={course}
      beforeSurvey={beforeSurvey}
      afterSurvey={afterSurvey}
    />
  );
}
